import { useRequiredContext } from "@redotech/react-util/context";
import { getAnalyticsReportsHrefForPath } from "@redotech/redo-merchant-analytics-app/analytics/reports/custom/custom-reports-section-href";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  confirmationPromptForCustomAnalyticsReportAction,
  type CustomAnalyticsReportAction,
} from "../../custom-analytics-report-tool-actions";
import { useChatMessagesRef } from "../../escalation/chat-messages-context";
import type { AskRedoUIMessage } from "../../types";
import * as styles from "./custom-analytics-report-actions.module.css";

const CUSTOM_ANALYTICS_REPORT_LINK_PREFIX =
  "/order-management/analytics/reports/custom/";

export const CustomAnalyticsReportActionButton = memo(
  function CustomAnalyticsReportActionButton({
    action,
    disabled,
    onSendMessage,
    toolCallId,
  }: {
    action: CustomAnalyticsReportAction;
    disabled: boolean;
    onSendMessage: (text: string) => void;
    toolCallId: string;
  }) {
    const messagesRef = useChatMessagesRef();
    const hasLaterUserResponse =
      action.type === "confirm" &&
      hasLaterUserMessage(messagesRef.current, toolCallId);

    if (action.type === "confirm" && hasLaterUserResponse && !action.plan) {
      return null;
    }

    if (action.type === "view-report") {
      return <CustomAnalyticsReportViewButton url={action.url} />;
    }

    return (
      <CustomAnalyticsReportConfirmCard
        action={action}
        disabled={disabled}
        hideAction={hasLaterUserResponse}
        onSendMessage={onSendMessage}
      />
    );
  },
);

const CustomAnalyticsReportConfirmCard = memo(
  function CustomAnalyticsReportConfirmCard({
    action,
    disabled,
    hideAction,
    onSendMessage,
  }: {
    action: Extract<CustomAnalyticsReportAction, { type: "confirm" }>;
    disabled: boolean;
    hideAction: boolean;
    onSendMessage: (text: string) => void;
  }) {
    const [pending, setPending] = useState(false);
    const handleClick = useCallback(() => {
      if (pending) {
        return;
      }
      setPending(true);
      onSendMessage(action.message);
    }, [action.message, onSendMessage, pending]);

    if (action.plan) {
      return (
        <Flex
          bgColor="primary"
          borderColor="secondary"
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          className={styles.planCard}
          dir="column"
          gap="xl"
          p="lg"
        >
          <Text fontSize="md" fontWeight="semibold">
            {action.plan.title}
          </Text>

          {action.plan.description && (
            <Text fontSize="sm" textColor="secondary">
              {action.plan.description}
            </Text>
          )}

          <Flex dir="column" gap="lg">
            {action.plan.sections.map((section) => (
              <Flex dir="column" gap="sm" key={section.title}>
                <Text fontSize="sm" fontWeight="semibold">
                  {section.title}
                </Text>
                <Flex
                  as="ul"
                  className={styles.planList}
                  dir="column"
                  gap="xs"
                  m="none"
                  pl="2xl"
                >
                  {section.values.map((value) => (
                    <li key={value}>
                      <Text fontSize="sm" textColor="secondary">
                        {value}
                      </Text>
                    </li>
                  ))}
                </Flex>
              </Flex>
            ))}
          </Flex>

          {!hideAction && (
            <Flex dir="column" gap="md" w="full">
              <Text fontSize="sm" textColor="primary">
                {confirmationPromptForCustomAnalyticsReportAction(action)}
              </Text>
              <RedoButton
                disabled={disabled || pending}
                hierarchy="primary"
                onClick={handleClick}
                size="sm"
                text={action.label}
              />
            </Flex>
          )}
        </Flex>
      );
    }

    return (
      <Flex justify="flex-start">
        <RedoButton
          disabled={disabled || pending}
          hierarchy="primary"
          onClick={handleClick}
          size="sm"
          text={action.label}
        />
      </Flex>
    );
  },
);

const CustomAnalyticsReportViewButton = memo(
  function CustomAnalyticsReportViewButton({ url }: { url: string }) {
    const routes = useRequiredContext(MerchantRoutesContext);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const href = normalizeReportUrl(
      url,
      getAnalyticsReportsHrefForPath(pathname, routes),
    );

    return (
      <Flex justify="flex-start">
        <RedoButton
          hierarchy="secondary"
          onClick={() => navigate(href)}
          size="sm"
          text="View custom report"
        />
      </Flex>
    );
  },
);

export function hasLaterUserMessage(
  messages: readonly AskRedoUIMessage[],
  toolCallId: string,
): boolean {
  const toolCallMessageIndex = messages.findIndex((message) =>
    message.parts.some(
      (part) => part.type === "dynamic-tool" && part.toolCallId === toolCallId,
    ),
  );
  if (toolCallMessageIndex < 0) {
    return false;
  }

  return messages
    .slice(toolCallMessageIndex + 1)
    .some((message) => message.role === "user");
}

export function stripCustomReportMarkdownLink(text: string): string {
  return text
    .replace(/\n{0,2}\[View custom report\]\([^)]+\)\s*$/u, "")
    .trimEnd();
}

export function normalizeReportUrl(
  url: string,
  analyticsReportsHref: string,
): string {
  if (url.startsWith(analyticsReportsHref)) {
    return url;
  }

  const customReportId = getCustomAnalyticsReportIdFromUrl(url);
  if (customReportId) {
    return `${analyticsReportsHref}/custom/${customReportId}`;
  }

  return url;
}

function getCustomAnalyticsReportIdFromUrl(url: string): string | null {
  let path = url;

  try {
    path = new URL(url).pathname;
  } catch {
    path = url;
  }

  if (!path.startsWith(CUSTOM_ANALYTICS_REPORT_LINK_PREFIX)) {
    return null;
  }

  return path.slice(CUSTOM_ANALYTICS_REPORT_LINK_PREFIX.length) || null;
}
